import type { BoardCard, BoardGroupBy } from '@/api/boardsApi'
import type { UpdateTicketInput } from '@/api/ticketsApi'

/**
 * Board columns and drag rules (z8nrz7fxzq). Pure, so the rules are tested
 * without a DOM. The API stays the authority: a refused move is reverted.
 */

/** Status columns in lifecycle order. `closed` is never a column: the auto-close sweep closes tickets. */
export const STATUS_COLUMNS = ['new', 'in_progress', 'waiting_on_customer', 'waiting_on_vendor', 'scheduled', 'escalated_engineering', 'resolved'] as const
export const PRIORITY_COLUMNS = ['P1', 'P2', 'P3', 'P4'] as const
export const UNASSIGNED = '__unassigned__'

export interface BoardColumn {
  id: string
  /** For assignee columns, the person's name; status / priority columns are labelled by the UI. */
  name?: string
  cards: BoardCard[]
}

export interface Person {
  id: string
  name: string
}

export function columnIdOf(card: BoardCard, groupBy: BoardGroupBy): string {
  if (groupBy === 'status') return card.status
  if (groupBy === 'priority') return card.priority.toUpperCase()
  return card.assignee?.id ?? UNASSIGNED
}

/**
 * The columns for a grouping, cards kept in the API's (SLA) order. Assignee
 * columns: Unassigned, then `me` (always — a place to drop work on yourself),
 * then everyone who holds a card, busiest first. Agents with nothing on this
 * board get no column (the card menu still assigns to anyone), so a desk with
 * many agents doesn't bury the people who have work.
 */
export function buildColumns(
  cards: readonly BoardCard[],
  groupBy: BoardGroupBy,
  agents: readonly Person[] = [],
  me?: Person | null,
): BoardColumn[] {
  const byColumn = new Map<string, BoardCard[]>()
  for (const c of cards) {
    const id = columnIdOf(c, groupBy)
    byColumn.set(id, [...(byColumn.get(id) ?? []), c])
  }
  const take = (id: string, name?: string): BoardColumn => ({ id, ...(name ? { name } : {}), cards: byColumn.get(id) ?? [] })
  if (groupBy === 'status') return STATUS_COLUMNS.map((s) => take(s))
  if (groupBy === 'priority') return PRIORITY_COLUMNS.map((p) => take(p))
  const nameOf = new Map<string, string>(agents.map((a) => [a.id, a.name]))
  for (const c of cards) if (c.assignee) nameOf.set(c.assignee.id, nameOf.get(c.assignee.id) ?? c.assignee.name)
  const holders = [...byColumn.keys()]
    .filter((id) => id !== UNASSIGNED && id !== me?.id)
    .sort((a, b) => (byColumn.get(b)?.length ?? 0) - (byColumn.get(a)?.length ?? 0) || (nameOf.get(a) ?? '').localeCompare(nameOf.get(b) ?? ''))
  return [take(UNASSIGNED), ...(me ? [take(me.id, nameOf.get(me.id) ?? me.name)] : []), ...holders.map((id) => take(id, nameOf.get(id)))]
}

export interface MovePermissions {
  update: boolean
  assign: boolean
  resolve: boolean
}

/**
 * What dropping `card` on `columnId` does: nothing (same column), a plain
 * update, the Resolve dialog, or nothing because it is not allowed.
 */
export function dropOutcome(card: BoardCard, groupBy: BoardGroupBy, columnId: string, can: MovePermissions): 'same' | 'update' | 'resolve' | 'blocked' {
  if (columnIdOf(card, groupBy) === columnId) return 'same'
  if (groupBy === 'assignee') return can.assign ? 'update' : 'blocked'
  if (groupBy === 'priority') return can.update ? 'update' : 'blocked'
  // Status: resolving keeps its dialog (reason + summary); anything else must be a move the state machine allows.
  if (columnId === 'resolved') return can.resolve && card.status !== 'closed' ? 'resolve' : 'blocked'
  return can.update && card.allowedTransitions.includes(columnId) ? 'update' : 'blocked'
}

/** The PATCH body for a plain move. */
export function moveInput(groupBy: BoardGroupBy, columnId: string): UpdateTicketInput {
  if (groupBy === 'status') return { status: columnId }
  if (groupBy === 'priority') return { priority: columnId }
  return { assigneeId: columnId === UNASSIGNED ? null : columnId }
}

/** The cards with one moved, for an optimistic update before the API answers. */
export function applyMove(cards: readonly BoardCard[], cardId: string, groupBy: BoardGroupBy, columnId: string, agents: readonly Person[] = []): BoardCard[] {
  return cards.map((c) => {
    if (c.id !== cardId) return c
    if (groupBy === 'status') return { ...c, status: columnId }
    if (groupBy === 'priority') return { ...c, priority: columnId }
    if (columnId === UNASSIGNED) return { ...c, assignee: null }
    const who = agents.find((a) => a.id === columnId)
    const name = who?.name ?? c.assignee?.name ?? ''
    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
    return { ...c, assignee: { id: columnId, name, initials, email: c.assignee?.email ?? '' } }
  })
}
